import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ThemeColors, Typography, BorderRadius, Transitions } from '../../styles/ThemeStyles';

interface AccessibleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const getVariantStyles = (variant: string) => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: ThemeColors.primary.main,
        color: ThemeColors.primary.contrastText,
        border: 'none',
        hoverBackgroundColor: ThemeColors.primary.dark
      };
    case 'secondary':
      return {
        backgroundColor: ThemeColors.secondary.main,
        color: ThemeColors.secondary.contrastText,
        border: 'none',
        hoverBackgroundColor: ThemeColors.secondary.dark
      };
    case 'danger':
      return {
        backgroundColor: ThemeColors.error.main,
        color: ThemeColors.error.contrastText || '#FFFFFF',
        border: 'none',
        hoverBackgroundColor: ThemeColors.error.dark
      };
    case 'tertiary':
    default:
      return {
        backgroundColor: 'transparent',
        color: ThemeColors.primary.main,
        border: `1px solid ${ThemeColors.primary.main}`,
        hoverBackgroundColor: `${ThemeColors.primary.main}10`
      };
  }
};

const StyledButton = styled.button<{
  variant: string;
  size: string;
  fullWidth: boolean;
  iconPosition: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: ${BorderRadius.md};
  font-family: ${Typography.fontFamily.primary};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${props => 
    props.size === 'sm' ? Typography.fontSize.sm :
    props.size === 'lg' ? Typography.fontSize.lg :
    Typography.fontSize.md
  };
  padding: ${props => 
    props.size === 'sm' ? '8px 16px' :
    props.size === 'lg' ? '16px 32px' :
    '12px 24px'
  };
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  ${({ variant }) => {
    const styles = getVariantStyles(variant);
    return `
      background-color: ${styles.backgroundColor};
      color: ${styles.color};
      border: ${styles.border};
      
      &:hover:not(:disabled) {
        background-color: ${styles.hoverBackgroundColor};
        transform: translateY(-1px);
      }
      
      &:focus {
        outline: 2px solid ${ThemeColors.primary.main};
        outline-offset: 2px;
      }
      
      &:active:not(:disabled) {
        transform: translateY(0);
      }
    `;
  }}
  
  &:disabled {
    cursor: not-allowed;
  }
  
  // Screen reader only text
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const LoadingSpinner = styled.div`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

export const AccessibleButton: React.FC<AccessibleButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  const isDisabled = disabled || loading;
  
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      iconPosition={iconPosition}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      {...props}
    >
      {loading && (
        <>
          <LoadingSpinner />
          <span className="sr-only">{t('common.loading', 'Loading...')}</span>
        </>
      )}
      
      {!loading && icon && iconPosition === 'left' && icon}
      
      {!loading && children && (
        <span>{children}</span>
      )}
      
      {!loading && icon && iconPosition === 'right' && icon}
      
      {loading && (
        <span className="sr-only">{t('common.loadingPleaseWait', 'Loading, please wait')}</span>
      )}
    </StyledButton>
  );
};
